import * as React from "react";
import { Stack } from "@mui/material";
import { StatusCard } from "@/components/dashboard/StatusCard";
import { MetricCard } from "@/components/dashboard/MetricCard";
import { HealthBadge } from "@/components/dashboard/HealthBadge";
import { QueryStateBoundary } from "@/components/dashboard/QueryStateBoundary";
import { useBrokerStatus } from "@/hooks/queries/useBrokerStatus";
import { useDashboardSystem } from "@/hooks/queries/useDashboardSystem";

interface BrokerStatusPayload {
  statuses: Record<string, string>;
}

interface DashboardSystemPayload {
  providers: Array<{ provider_id: string; connection_status: string }>;
}

/** Reuses Sprint 3.2's `useBrokerStatus` and `useDashboardSystem` hooks rather than duplicating them. */
export function ProviderHealthSection(): React.JSX.Element {
  const statusQuery = useBrokerStatus();
  const dashboardSystemQuery = useDashboardSystem();

  return (
    <StatusCard title="Provider Health">
      <Stack spacing={1.5}>
        <QueryStateBoundary
          query={statusQuery}
          isEmpty={(data) => Object.keys((data as unknown as BrokerStatusPayload).statuses).length === 0}
          emptyMessage="No provider status reported by the backend."
        >
          {(payload) => {
            const statuses = (payload as unknown as BrokerStatusPayload).statuses;
            const summary = Object.entries(statuses)
              .map(([providerId, status]) => `${providerId}: ${status}`)
              .join(", ");
            const overallStatus = Object.values(statuses)[0] ?? null;
            return (
              <>
                <MetricCard label="Health" render={<HealthBadge status={overallStatus} />} />
                <MetricCard label="Connection Status" value={summary} />
              </>
            );
          }}
        </QueryStateBoundary>
        <QueryStateBoundary query={dashboardSystemQuery}>
          {(payload) => {
            const providers = (payload as unknown as DashboardSystemPayload).providers ?? [];
            const sessionSummary =
              providers.length > 0
                ? providers.map((p) => `${p.provider_id}: ${p.connection_status}`).join(", ")
                : null;
            return <MetricCard label="Session Status" value={sessionSummary} />;
          }}
        </QueryStateBoundary>
        {/* Neither BrokerSummaryDto nor SystemDashboardDto carries a
            distinct authentication-state or last-heartbeat field. */}
        <MetricCard label="Authentication State" value={null} />
        <MetricCard label="Last Heartbeat" value={null} />
      </Stack>
    </StatusCard>
  );
}
